import { safeScan } from '../lexicons/regex-list.ts';
/**
 * SF-04 Pseudo-precision (INV-CH-SF-04; kept).
 *
 * Per unsourced prose sentence (a sentence with a citation guard — "(1", "et al", "according to",
 * "study", … — is skipped): an unsourced clock time ×2 each, a percentage in a prescriptive
 * sentence ×1 each, and a prescriptive sentence with any bare integer ×0.5 (once per sentence).
 * Rate per 1k prose words.
 */
import {
	compiledEnglish,
	line,
	nativeMeasure,
	proseBlocks,
	rateDef,
	sentenceRange,
	wordExposure,
} from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import { proseSentenceList } from './helpers.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

const W_CLOCK = 2;
const W_PCT = 1;
const W_WEAK = 0.5;
const HAS_DIGIT = /\d/;

export const sf04Def = rateDef({
	id: 'SF-04',
	name: 'Pseudo-precision',
	family: 'TEXTURE',
	features: [
		{ id: 'clock', label: 'Unsourced clock time' },
		{ id: 'percentage', label: 'Prescriptive percentage' },
		{ id: 'weak', label: 'Prescriptive sentence with a number' },
	],
	oneWay: true,
	minExposure: 350,
	exposureUnit: 'words',
	lang: 'native',
	ramp: [
		[0, 12],
		[1, 30],
		[3, 52],
		[6, 70],
		[12, 88],
	],
	unitScale: 1000,
	unitLabel: 'precision units per 1k words',
	phi0: 1.5,
	weight: 1.0,
	nd: 0.15,
	// events are scored per document-model sentence (C-SPL): the seed fused a heading or blockquote
	// into the next sentence, so digits in "Chapter 48" satisfied the integer test of a quoted sentence
	changes: ['C-SPL'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const nr = compiledEnglish().channels['ch-sf04'];
	const cite = nr.get('citeGuard');
	const clockRe = nr.get('clockTime');
	const pctRe = nr.get('percentage');
	const prescRe = nr.get('prescriptive');
	const intRe = nr.get('integer');
	const k = new Float64Array(doc.paragraphs.count);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	const sl = proseSentenceList(doc);
	const summary: string[] = [];
	let n = 0;
	for (const b of proseBlocks(doc)) {
		// every event needs a digit; skip digit-free blocks outright
		if (!HAS_DIGIT.test(b.text)) continue;
		for (let i = sl.paraFirst[b.p] as number; i < (sl.paraFirst[b.p + 1] as number); i++) {
			const { start, end } = sentenceRange(doc, i);
			const s = doc.text.slice(start, end);
			if (!HAS_DIGIT.test(s)) continue;
			if (cite.test(s)) continue;
			const clocks: string[] = [];
			const pcts: string[] = [];
			const clockSpans: number[] = [];
			const pctSpans: number[] = [];
			safeScan(clockRe, s, start, (a, z, m) => {
				clocks.push(m[0]);
				clockSpans.push(a, z);
			});
			safeScan(pctRe, s, start, (a, z, m) => {
				pcts.push(m[0]);
				pctSpans.push(a, z);
			});
			const presc = prescRe.test(s);
			let kk = 0;
			if (clocks.length) {
				for (let j = 0; j < clockSpans.length; j += 2)
					ev.add(clockSpans[j] as number, clockSpans[j + 1] as number, 0, W_CLOCK);
				kk += clocks.length * W_CLOCK;
				if (summary.length < 8)
					summary.push(line(clocks.join(', '), 'prescribed clock time, unsourced'));
			}
			if (pcts.length && presc) {
				for (let j = 0; j < pctSpans.length; j += 2)
					ev.add(pctSpans[j] as number, pctSpans[j + 1] as number, 1, W_PCT);
				kk += pcts.length * W_PCT;
				if (summary.length < 8)
					summary.push(line(pcts.join(', '), 'prescriptive percentage, unsourced'));
			} else if (presc && intRe.test(s)) {
				ev.add(start, end, 2, W_WEAK);
				kk += W_WEAK;
			}
			if (kk > 0) {
				k[b.p] = (k[b.p] as number) + kk;
				n += kk;
			}
		}
	}
	return { k, e, evidence: ev, summary, features: { precisionUnits: n } };
}

export const sf04: ChannelImpl = rateChannel(sf04Def, nativeMeasure(measure));
export { sf04Def as def, sf04 as impl };
